import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { isFileNameValid, noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { ToastMessageOptions } from 'primeng/api';
import { DocumentsPAC, UploadError } from '@shared/model';
import { DocumentUtilities } from '@shared/utility';
import { DocumentTypes } from '../document-pacim-dropdown.constant';

@Component({
    selector: 'upload-document-pacim',
    templateUrl: './upload-documents-pacim.component.html',
    styleUrls: ['./upload-documents-pacim.component.scss'],
    standalone: false
})
export class UploadDocumentsPACIMComponent implements OnInit, OnChanges {
  @Input() disableComponent = false;
  @Input() documents!: DocumentsPAC[];

  @Output() uploadedNewFile = new EventEmitter<DocumentsPAC[]>();

  descriptionForm!: FormGroup;
  displayDialog = false;
  showButton = false;
  totalFilesSize!: number;
  fileTypeErrors = false;
  fileSizeErrors = false;
  fileNameInvalidErrors = false;
  selectedDocumentTypes: string[] = [];
  uploadedFiles: DocumentsPAC[] = [];
  messageContent?: ToastMessageOptions;
  documentTypesList!: {
    label: string,
    value: string
  }[];

  readonly allowedExtensions: string[] = ['.jpg', '.jpeg', '.png', '.pdf'];
  readonly maxFileSize = 20 * 1024 * 1024; // 20Mb
  readonly maxFileNameLength = 130;

  constructor(
    private fb: FormBuilder,
    private ref: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    const { documents } = changes;

    if (documents && documents.currentValue) {
      this.uploadedFiles = documents.currentValue ? [...documents.currentValue] : [];

      this.getTotalFilesSize();
    }
  }

  ngOnInit() {
    this.documentTypesList = DocumentTypes;
    this.buildForm();
  }

  deleteItem(event: number) {
    this.uploadedFiles.splice(event, 1);
    this.resetUploadFields();
    this.getTotalFilesSize();

    this.uploadedNewFile.emit(this.uploadedFiles);
  }

  showUploadButton(documentTypes: string[]) {
    this.showButton = documentTypes.length > 0;
  }

  onError(errors: UploadError[]) {
    this.fileTypeErrors = errors.some((e) => e.type === 'accept');
    this.fileSizeErrors = errors.some((e) => e.type === 'fileSize');
    this.fileNameInvalidErrors = errors.some((e) => e.type === 'fileName');
    this.showDialog();
    this.hideButton();
    this.resetUploadFields();
  }

  addFile(files: File[]) {
    this.addSelected(files);
  }

  async addSelected(files: FileList | File[]) {
    const file: File = files[0];
    const indexNumber = this.uploadedFiles ? this.uploadedFiles.length : 0;

    if (file.name.length > this.maxFileNameLength) {
      this.showMessage();
    }

    if (this.maxFileSize && (this.totalFilesSize + file.size > this.maxFileSize)) {
      const error: UploadError[] = [{type: 'fileSize', name: file.name}];
      this.onError(error);
    } else if (!isFileNameValid(file.name).valid) {
      const error: UploadError[] = [{type: 'fileName', name: file.name}];
      this.onError(error);
    } else {
      this.resetValidationErrors();

      try {
        const fileBase64: string = (await this.readFileAsync(file)) as string;
        const fileContent = fileBase64.split(',')[1];

        this.uploadedFiles[indexNumber] = {
          fileMetaData: {
            size: file.size,
            type: file.type,
          },
          documentFileName: DocumentUtilities.addFilenameTimestamp(file.name, this.maxFileNameLength),
          fileContent,
          documentTypes: this.selectedDocumentTypes,
          documentSource: 'Upload',
          dateTimeFaxReceived: new Date().toUTCString(),
          description: this.descriptionForm.get('fileDescription')?.value,
        };

        this.hideButton();
        this.resetUploadFields();
        this.getTotalFilesSize();

        this.uploadedNewFile.emit(this.uploadedFiles);
      } catch (error) {
        console.log("Upload file error: ", error);
      }
    }

    this.ref.markForCheck();
  }

  resetValidationErrors() {
    this.fileTypeErrors = false;
    this.fileSizeErrors = false;
    this.fileNameInvalidErrors = false;
  }

  showDialog() {
    this.displayDialog = true;
  }

  private resetUploadFields() {
    this.descriptionForm.reset();
    this.selectedDocumentTypes = [];
  }

  private getTotalFilesSize() {
    this.totalFilesSize = this.uploadedFiles.reduce((sum, { fileMetaData }) => {
      let size = 0;

      if (fileMetaData?.size) {
        size = fileMetaData.size;
      }
      return sum + size
    }, 0);
  }

  private hideButton(): void {
    this.showButton = false;
  }

  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reader: any = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private buildForm(): void {
    this.descriptionForm = this.fb.group({
      fileDescription: [{ disabled: false, value: '' }, [Validators.maxLength(25), noWhiteSpaceValidator]]
    });
  }

  private showMessage(): void {
    this.messageContent = {
      severity: 'warn',
      summary: 'Warning',
      detail: `The selected file’s name exceeds the 130 characters limit.
        The file name will have to be cropped to the appropriate length.`
    };

    setTimeout(() => {
      this.messageContent = null;
      this.ref.markForCheck()
    }, 10000);
  }
}
